import type { CategoryNavigationItem } from '#/components/layout/CategoryNavigation';
import type { CategorySlug, ProductTypeSlug, SectorSlug } from '#/types';

export type ApiInvestmentType = 'equity' | 'etf' | 'mutual_fund';

export const PRODUCT_TYPE_SLUGS: ProductTypeSlug[] = [
  'equities',
  'mutual-funds',
  'etfs',
];

export const SECTOR_SLUGS: SectorSlug[] = [
  'basic-materials',
  'communication-services',
  'consumer-cyclical',
  'consumer-defensive',
  'energy',
  'financial-services',
  'healthcare',
  'industrials',
  'real-estate',
  'technology',
  'utilities',
];

export const PRODUCT_TYPE_BREADCRUMB_LABELS: Record<ProductTypeSlug, string> = {
  equities: 'Equities',
  'mutual-funds': 'Mutual Funds',
  etfs: 'Exchange-Traded Funds',
};

/**
 * Card titles for the Related Topics section — distinct from the breadcrumb
 * labels: equities is disambiguated as "Equities (Stocks)" and ETFs uses its
 * short form.
 */
export const RELATED_PRODUCT_TYPE_TITLES: Record<ProductTypeSlug, string> = {
  equities: 'Equities (Stocks)',
  'mutual-funds': 'Mutual Funds',
  etfs: 'ETFs',
};

export const PRODUCT_TYPE_DESCRIPTIONS: Record<ProductTypeSlug, string> = {
  equities:
    'When you invest in stock, you buy ownership shares in a company-also known as equity shares.',
  'mutual-funds':
    'Mutual funds pool the money of many investors to purchase a range of securities.',
  etfs: 'ETFs generally combine aspects of mutual funds and conventional stocks.',
};

const PRODUCT_TYPE_SLUG_BY_API_INVESTMENT_TYPE: Record<
  ApiInvestmentType,
  ProductTypeSlug
> = {
  equity: 'equities',
  etf: 'etfs',
  mutual_fund: 'mutual-funds',
};

const API_INVESTMENT_TYPE_LABELS: Record<ApiInvestmentType, string> = {
  equity: 'equity',
  etf: 'ETF',
  mutual_fund: 'mutual fund',
};

export const SECTOR_LABELS: Record<SectorSlug, string> = {
  'basic-materials': 'Basic Materials',
  'communication-services': 'Communication Services',
  'consumer-cyclical': 'Consumer Cyclical',
  'consumer-defensive': 'Consumer Defensive',
  energy: 'Energy',
  'financial-services': 'Financial Services',
  healthcare: 'Healthcare',
  industrials: 'Industrials',
  'real-estate': 'Real Estate',
  technology: 'Technology',
  utilities: 'Utilities',
};

export const SECTOR_DESCRIPTIONS: Record<SectorSlug, string> = {
  'basic-materials':
    'Companies producing raw materials used in manufacturing, chemicals, and construction.',
  'communication-services':
    'Businesses focused on media, telecom, and digital communication platforms.',
  'consumer-cyclical':
    'Companies tied to discretionary spending that often move with the broader economy.',
  'consumer-defensive':
    'Businesses selling everyday essentials that tend to remain steady across market cycles.',
  energy:
    'Companies involved in oil, gas, power, and the infrastructure that supports energy production.',
  'financial-services':
    'Businesses providing banking, insurance, asset management, and other financial products.',
  healthcare:
    'Companies spanning medical care, pharmaceuticals, biotech, and healthcare services.',
  industrials:
    'Businesses supporting manufacturing, transportation, logistics, and commercial infrastructure.',
  'real-estate':
    'Companies focused on property ownership, development, and real-estate-related services.',
  technology:
    'Companies building software, hardware, semiconductors, and digital infrastructure.',
  utilities:
    'Businesses delivering essential services like electricity, water, and natural gas.',
};

/**
 * Morningstar's three "super sectors." A sector page's Related Topics cards
 * link to the other sectors within its own group.
 */
export const SECTOR_SUPER_GROUPS: SectorSlug[][] = [
  ['basic-materials', 'consumer-cyclical', 'financial-services', 'real-estate'],
  ['consumer-defensive', 'healthcare', 'utilities'],
  ['communication-services', 'energy', 'industrials', 'technology'],
];

export const ICON_LABELS: Record<CategorySlug, string> = {
  equities: 'Investment Product',
  'mutual-funds': 'Investment Product',
  etfs: 'Investment Product',
  'basic-materials': 'Market Sector',
  'communication-services': 'Market Sector',
  'consumer-cyclical': 'Market Sector',
  'consumer-defensive': 'Market Sector',
  energy: 'Market Sector',
  'financial-services': 'Market Sector',
  healthcare: 'Market Sector',
  industrials: 'Market Sector',
  'real-estate': 'Market Sector',
  technology: 'Market Sector',
  utilities: 'Market Sector',
};

export const INTERIOR_NAV_PRODUCT_TYPES: CategoryNavigationItem[] = [
  {
    label: 'Equities',
    href: '/equities',
    description: PRODUCT_TYPE_DESCRIPTIONS.equities,
  },
  {
    label: 'Mutual Funds',
    href: '/mutual-funds',
    description: PRODUCT_TYPE_DESCRIPTIONS['mutual-funds'],
  },
  {
    label: 'ETFs',
    href: '/etfs',
    description: PRODUCT_TYPE_DESCRIPTIONS.etfs,
  },
];

export const SECTOR_SLUG_BY_LABEL = Object.fromEntries(
  Object.entries(SECTOR_LABELS).map(([slug, label]) => [label, slug]),
) as Partial<Record<string, SectorSlug>>;

export const INTERIOR_NAV_SECTORS: CategoryNavigationItem[] = SECTOR_SLUGS.map(
  (slug) => ({
    label: SECTOR_LABELS[slug],
    href: `/${slug}`,
  }),
);

export const INTERIOR_NAV_CTA: CategoryNavigationItem = {
  label: 'Bond Data',
  href: 'https://www.finra.org/finra-data/fixed-income',
  description: "Visit FINRA's Fixed Income Data center.",
  target: '_blank',
};

export function getCategoryIconLabel(category: CategorySlug) {
  return ICON_LABELS[category];
}

export function getProductTypeSlugForInvestmentType(
  investmentType: ApiInvestmentType,
): ProductTypeSlug {
  return PRODUCT_TYPE_SLUG_BY_API_INVESTMENT_TYPE[investmentType];
}

export function getInvestmentTypeLabel(investmentType: ApiInvestmentType) {
  return API_INVESTMENT_TYPE_LABELS[investmentType];
}

export function getDetailHrefForInvestmentType(
  investmentType: ApiInvestmentType,
  symbol: string,
) {
  return `/${getProductTypeSlugForInvestmentType(investmentType)}/${symbol}`;
}

export function getCategoryBreadcrumbLabel(category: string) {
  if (isProductTypeCategory(category)) {
    return PRODUCT_TYPE_BREADCRUMB_LABELS[category];
  }

  if (isSectorCategory(category)) {
    return SECTOR_LABELS[category];
  }

  return category;
}

export function isProductTypeCategory(
  category: string,
): category is ProductTypeSlug {
  return PRODUCT_TYPE_SLUGS.includes(category as ProductTypeSlug);
}

export function isSectorCategory(category: string): category is SectorSlug {
  return SECTOR_SLUGS.includes(category as SectorSlug);
}

export function isSupportedCategory(category: string) {
  return isProductTypeCategory(category) || isSectorCategory(category);
}

export function getInteriorCategoryActiveHref(category: string) {
  return isSupportedCategory(category) ? `/${category}` : '';
}

/** Related Topics card */
export interface RelatedCategoryTopic {
  slug: CategorySlug;
  eyebrow: string;
  title: string;
  description: string;
  href: string;
}

export function getRelatedCategoryTopics(
  category: string,
): RelatedCategoryTopic[] {
  if (isProductTypeCategory(category)) {
    return PRODUCT_TYPE_SLUGS.filter((slug) => slug !== category).map(
      (slug) => ({
        slug,
        eyebrow: 'Investment Product',
        title: RELATED_PRODUCT_TYPE_TITLES[slug],
        description: PRODUCT_TYPE_DESCRIPTIONS[slug],
        href: `/${slug}`,
      }),
    );
  }

  if (isSectorCategory(category)) {
    const group =
      SECTOR_SUPER_GROUPS.find((sectors) => sectors.includes(category)) ?? [];

    return group
      .filter((slug) => slug !== category)
      .map((slug) => ({
        slug,
        eyebrow: 'Sector',
        title: SECTOR_LABELS[slug],
        description: SECTOR_DESCRIPTIONS[slug],
        href: `/${slug}`,
      }));
  }

  return [];
}
